// Loose-KTX2 texture config (DECISION-053 Slice-2, Step 1) — PURE (no THREE / no DOM) so it
// unit-tests headlessly. Decides how a LOOSE `.ktx2` map (not GLB-embedded) is interpreted
// per PBR role, and the wrapper in _gltf-loader-factory.js applies the result to the decoded
// CompressedTexture. The three moving parts the spec calls out:
//
//   • colorSpace — COLOR data (baseColor/albedo/emissive) is sRGB-encoded; DATA maps
//     (normal / roughness / metalness / ao / height) are LINEAR. Getting this wrong washes
//     out normals + shifts roughness. THREE colorSpace values are stable public API
//     (r183: SRGBColorSpace="srgb", NoColorSpace="") so we return them verbatim — the loader
//     assigns `tex.colorSpace = cfg.colorSpace` directly.
//   • flipY — KTX2/basis is authored flipY=false (the glTF UV convention). A loose map applied
//     to glTF UVs MUST match or it inverts vertically. (Compressed textures can't flip at
//     upload anyway, so false is also the only valid value.)
//   • wrap + anisotropy — shell materials tile, so RepeatWrapping by default; anisotropy keeps
//     grazing-angle floors/walls crisp. wrap is returned as a token the loader maps to the
//     THREE constant (kept out of this pure module).

// PBR roles whose data is COLOR (sRGB-encoded). "map" is THREE's albedo slot.
const COLOR_ROLES = new Set(["basecolor", "albedo", "diffuse", "emissive", "emissivemap", "map"]);

// Known LINEAR data roles — enumerated for validation/telemetry; anything not in COLOR_ROLES
// is treated as linear data regardless, so this is the allow-list for knownRole().
const DATA_ROLES = new Set([
	"normal", "normalmap", "roughness", "roughnessmap", "metalness", "metalnessmap",
	"metallicroughness", "ao", "aomap", "occlusion", "height", "displacement", "bump", "orm",
]);

const SRGB = "srgb";        // === THREE.SRGBColorSpace
const NO_COLORSPACE = "";   // === THREE.NoColorSpace (linear data)

/** Is this PBR role sRGB colour data (vs linear data)? */
export function isColorRole(role) {
	return COLOR_ROLES.has(String(role || "").toLowerCase());
}

/** Is this a role we recognise (colour or data)? Unknown roles still resolve (as linear). */
export function knownRole(role) {
	const r = String(role || "").toLowerCase();
	return COLOR_ROLES.has(r) || DATA_ROLES.has(r);
}

/** How to interpret a loose `.ktx2` map for a PBR role. Pure — no THREE. */
export function resolveKtx2Config(role, { anisotropy = 8, wrap = "repeat" } = {}) {
	return {
		colorSpace: isColorRole(role) ? SRGB : NO_COLORSPACE,
		flipY: false,                          // KTX2 authored flipY=false (glTF UV convention)
		wrap: wrap === "clamp" ? "clamp" : "repeat",
		anisotropy: Math.max(1, Math.floor(anisotropy) || 1),
	};
}

/** Cache key — the SAME url can back two roles with different colourSpace (a greyscale packed
 * into both a roughness and an ao slot), so a url alone is not a safe key. Key by url + colour
 * class (c/d) — the only field that changes the decoded texture's interpretation. */
export function ktx2CacheKey(url, role) {
	return `${url}|${isColorRole(role) ? "c" : "d"}`;
}

export { COLOR_ROLES, DATA_ROLES };
